<script setup lang="ts">
import { computed, provide, reactive, ref, useTemplateRef } from "vue";
import type { ComputedRef, UnwrapNestedRefs } from "vue";
import { defu } from "defu";
import SwProductListingFilter from "./SwProductListingFilter.vue";
import type {
  CmsElementProductListing,
  CmsElementSidebarFilter,
} from "@shopware-pwa/composables-next";
import { useCmsTranslations } from "@shopware-pwa/composables-next";
import { useCategoryListing } from "#imports";
import { onClickOutside } from "@vueuse/core";
import { type LocationQueryRaw, useRoute, useRouter } from "vue-router";
import type { Schemas, operations } from "#shopware";
import SwCategoryFilter from "./SwCategoryFilter.vue";

const props = defineProps<{
  content: CmsElementProductListing | CmsElementSidebarFilter;
  listingType?: string;
}>();

type Translations = {
  listing: {
    filters: string;
    sort: string;
    resetFilters: string;
  };
};

let translations: Translations = {
  listing: {
    filters: "Filters",
    sort: "Sort",
    resetFilters: "Reset filters",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const route = useRoute();
const router = useRouter();

const isSortMenuOpen = ref(false);
const {
  changeCurrentSortingOrder,
  filtersToQuery,
  getCurrentFilters,
  getCurrentSortingOrder,
  getInitialFilters,
  search,
} = useCategoryListing();


const sidebarSelectedFilters: UnwrapNestedRefs<{
  [key: string]: any;
}> = reactive<{
  manufacturer: Set<string>;
  properties: Set<string>;
  "min-price": string | number | undefined;
  "max-price": string | number | undefined;
  rating: string | number | undefined;
  "shipping-free": boolean | undefined;
}>({
  manufacturer: new Set(),
  properties: new Set(),
  "min-price": undefined,
  "max-price": undefined,
  rating: undefined,
  "shipping-free": undefined,
});

const showResetFiltersButton = computed<boolean>(() => {
  if (
    selectedOptionIds.value.length !== 0 ||
    sidebarSelectedFilters["max-price"] ||
    sidebarSelectedFilters["min-price"] ||
    sidebarSelectedFilters["rating"] ||
    sidebarSelectedFilters["shipping-free"]
  ) {
    return true;
  }

  return false;
});

const searchCriteriaForRequest: ComputedRef<Schemas["ProductListingCriteria"]> =
  computed(() => ({
    // turn Set to array and then into string with | separator
    manufacturer: [...(sidebarSelectedFilters.manufacturer as string[])]?.join(
      "|"
    ),
    // turn Set to array and then into string with | separator
    properties: [...(sidebarSelectedFilters.properties as string[])]?.join("|"),
    "min-price": sidebarSelectedFilters["min-price"] as number,
    "max-price": sidebarSelectedFilters["max-price"] as number,
    order: getCurrentSortingOrder.value as string,
    "shipping-free": sidebarSelectedFilters["shipping-free"] as boolean,
    rating: sidebarSelectedFilters["rating"] as number,
    search: "",
    limit: route.query.limit ? Number(route.query.limit) : 24,
  }));

for (const param in route.query) {
  if (sidebarSelectedFilters.hasOwnProperty(param)) {
    if (
      sidebarSelectedFilters[param] &&
      typeof sidebarSelectedFilters[param] === "object"
    ) {
      (route.query[param] as unknown as string)
        .split("|")
        .forEach((element) => {
          sidebarSelectedFilters[param].add(element);
        });
    } else {
      sidebarSelectedFilters[param] = route.query[param];
    }
  }
}

const onOptionSelectToggle = async ({
  code,
  value,
}: {
  code: string;
  value: string;
}) => {
  if (!["properties", "manufacturer"].includes(code)) {
    sidebarSelectedFilters[code] = value;
  } else {
    if (sidebarSelectedFilters[code]?.has(value)) {
      sidebarSelectedFilters[code]?.delete(value);
    } else {
      sidebarSelectedFilters[code]?.add(value);
    }
  }

  await executeSearch();
};

const executeSearch = async () => {
  await search(searchCriteriaForRequest.value);
  const query = filtersToQuery(searchCriteriaForRequest.value);
  delete query.limit; // this will remove limit from the url query but still use it in the search
  await router.push({
    query: query as LocationQueryRaw,
  });
};

const clearFilters = () => {
  sidebarSelectedFilters.manufacturer.clear();
  sidebarSelectedFilters.properties.clear();
  sidebarSelectedFilters["min-price"] = undefined;
  sidebarSelectedFilters["max-price"] = undefined;
  sidebarSelectedFilters["rating"] = undefined;
  sidebarSelectedFilters["shipping-free"] = undefined;
};



const currentSortingOrder = computed({
  get: (): string => getCurrentSortingOrder.value || "",
  set: async (order: string): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        order,
      },
    });

    await changeCurrentSortingOrder(order, {
      ...(route.query as unknown as operations["searchPage post /search"]["body"]),
      limit: route.query.limit ? Number(route.query.limit) : 24,
    });
  },
});

const selectedOptionIds = computed(() => [
  ...sidebarSelectedFilters.properties,
  ...sidebarSelectedFilters.manufacturer,
]);
provide("selectedOptionIds", selectedOptionIds);

const getFilters = computed(() => {
  return getInitialFilters.value.filter((filter) => {
    return (
      filter.code !== "shipping-free" &&
      filter.code !== "rating" &&
      !filter.code.includes("counter")
    );
  });
});

async function invokeCleanFilters() {
  clearFilters();
  await executeSearch();
}
const isDefaultSidebarFilter =
  props.content.type === "sidebar-filter" &&
  props.content.config?.boxLayout?.value === "standard";
const dropdownElement = useTemplateRef("dropdownElement");
onClickOutside(dropdownElement, () => (isSortMenuOpen.value = false));

</script>
<template>
  <ClientOnly>
    <template v-if="getInitialFilters.length">
      <!-- Accordion for Categories -->
      <SwCategoryFilter/>
      <div
        v-for="filter in getFilters"
        :key="`${filter?.id || filter?.code}`"
        class="accordion-item border-b border-b-custom-grey overflow-hidden"
      >
        <SwProductListingFilter
          @select-filter-value="onOptionSelectToggle"
          :selected-filters="getCurrentFilters"
          :filter="filter"
          class="relative"
        />
      </div>
      <div v-if="showResetFiltersButton" class="w-full text-start mt-8 mb-2">
        <button
          class="flex items-center h-10 px-6 py-3 text-sm bg-dark-bg text-white rounded-full hover:bg-red-bg"
          @click="invokeCleanFilters"
          type="button"
        >
         {{ $t("listing.resetFilters") }}<span
            class="w-4 h-4 i-carbon-close-filled inline-block align-middle ml-2"
          ></span>
        </button>
      </div>
    </template>
  </ClientOnly>
</template>
